"use client"

import { useSyncStore } from "@/app/(store)/sync.store"
import { isDJUIEnabled } from "@/lib/featureFlags.utils"
import { cn } from "@/lib/utils"
import { Avatar, Indicator } from "@mantine/core"
import { makeURL } from "@nauverse/make-url"
import { useEffect, useState } from "react"

/**
 * Calculates the SHA-256 hash of a given message.
 * Found at https://gist.github.com/technikhil314/40f3c41e1039f4c7964843e47a9f25fc
 * which is based on https://stackoverflow.com/a/70891826
 *
 * @param message - The message to be hashed.
 * @returns A promise that resolves to the hexadecimal representation of the hash.
 */
async function digestMessage(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message) // encode as (utf-8) Uint8Array
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8) // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("") // convert bytes to hex string
  return hashHex
}

const BASE_AVATAR_SIZE = 28

export function UserProfileImageWithUserStatus({
  fullName,
  email,
  classNames,
  indicatorOffset,
  avatarSize,
}: {
  fullName: string
  email: string
  indicatorOffset?: number
  avatarSize?: number
  classNames?: {
    indicator?: string
    avatar?: string
  }
}) {
  const [gravatarURL, setGravatarURL] = useState<string>("")
  const [userStatus, setUserStatus] = useState<
    "checked-in" | "checked-out" | "unknown"
  >("unknown")
  const isCheckedIn = useSyncStore(
    (state) => state.userStatus?.checkedIn ?? false
  )

  useEffect(() => {
    // Gravatar email hash must be:
    // - A SHA-256 hash of the email address
    // - The email must be trimmed of any leading or trailing whitespace
    // - The email must be converted to all lowercase
    // https://docs.gravatar.com/general/hash/
    digestMessage(email.trim().toLowerCase()).then((hash) => {
      // URL built as explained at https://docs.gravatar.com/general/images/
      setGravatarURL(
        makeURL("https://gravatar.com/avatar/:hash", {
          params: {
            hash,
            s: avatarSize ?? BASE_AVATAR_SIZE, // Size in pixels
            // If we are not in the localhost, we set to use a dummy profile image
            // instead of the default dummy gravatar image when Gravatar service
            // does not find an image for the given email.
            // ...(window.location.host.includes("localhost") === false && {
            //   d: encodeURIComponent(
            //     `${window.location.host}/assets/dummy_profile.png`
            //   ),
            // }),
          },
        })
      )
    })
  }, [avatarSize, email])

  useEffect(() => {
    if (isCheckedIn) {
      setUserStatus("checked-in")
    } else {
      setUserStatus("checked-out")
    }
  }, [isCheckedIn])

  return (
    <Indicator
      color={
        userStatus === "checked-in"
          ? "green.5"
          : userStatus === "checked-out"
            ? "yellow.5"
            : "gray.5"
      }
      position="bottom-end"
      className={classNames?.indicator}
      classNames={{
        root: "!z-[7999]",
        indicator: cn(isDJUIEnabled() ? "" : "!hidden"),
      }}
      offset={indicatorOffset || 2}
    >
      <Avatar
        size="sm"
        radius="sm"
        alt={fullName}
        className={cn("ml-1", classNames?.avatar)}
        src={
          gravatarURL
            ? gravatarURL
            : `https://gravatar.com/avatar/f057011cb2ea8add1aa073b2ec2428175bc4935ef46112eb5ed5da2d816119b0/?s=${avatarSize ?? BASE_AVATAR_SIZE}`
        }
        style={{
          width: `${avatarSize ?? BASE_AVATAR_SIZE}px`,
          height: `${avatarSize ?? BASE_AVATAR_SIZE}px`,
        }}
      />
    </Indicator>
  )
}
